"use client";
import { forwardRef } from "@chakra-ui/react";
import type { ContentfulImageParams } from "@delicious-simplicity/next-image-contentful-loader";
import { contentfulLoader } from "@delicious-simplicity/next-image-contentful-loader";
import { isEmpty } from "lodash-es";
import type { ImageProps as _NextImageProps, ImageLoaderProps, StaticImageData } from "next/image";
import _NextImage from "next/image";
import { Box } from "../../layout/box/Box";
import type { FastlyImageOptimizerParams } from "./aprimo-loader";
import { aprimoLoader } from "./aprimo-loader";
import { FallbackImage } from "./FallbackImage";
import type { CSSProperties, SyntheticEvent } from "react";
import { useState } from "react";
import { useIsomorphicLayoutEffect } from "../../../hooks/useIsomorphicLayoutEffect";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "./imagePaths";
import { deviceSizes } from "../../../design-tokens/13.deviceSizes/deviceSizes";

export interface StaticRequire {
    default: StaticImageData;
}

export { contentfulLoader };

export type StaticImport = StaticRequire | StaticImageData;

export interface NextImageProps
    // omit the width / height props from Chakra style props - as these props have a special meaning in Next Image

    // Pick the Next Image props from their interface - if we just naively extend there will be type clashes because they omit certain HTMLImage props
    extends Omit<_NextImageProps, "sizes"> {
    /**
     * The image sizes to use when loading the image from contentful
     * should be a percent of viewport width, you can also use the value with wv
     */
    sizes?: string[];
    /**
     * Image Optimizer params for @fastly
     */
    fastlyImageParams?: FastlyImageOptimizerParams;
    objectFit?: CSSProperties["objectFit"];
    onLoadingComplete?: (result: { naturalWidth: number; naturalHeight: number }) => void;
}

export const isContentfulImage = (src: string | StaticImport): boolean =>
    typeof src === "string" && src?.includes("images.ctfassets.net");

export const isAprimoImage = (src: string | StaticImport): boolean =>
    typeof src === "string" && src?.includes("aprimocdn");

export const isFlashCDNImage = (src: string | StaticImport): boolean =>
    typeof src === "string" && src?.includes("flashedgecdn");

export const isWistiaImage = (src: string | StaticImport): boolean =>
    typeof src === "string" && (src.includes("wistia.com") || src.includes("wi.st"));

export const isPlaceholderImage = (src: string): boolean =>
    !src || (typeof src === "string" && src?.includes(PRODUCT_IMAGE_PLACEHOLDER_URL));

export const protocolRelativeToAbsoluteUrl = (url: string) => {
    if (url?.startsWith("//")) {
        return `https:${url}`;
    }
    return url;
};

export const isStaticImage = (src: string | StaticImport): boolean => {
    if (isEmpty(src) || typeof src === "string") {
        return false;
    }

    return !!("default" in src || "src" in src);
};

const useImageFallback = (src: string | StaticImport) => {
    const [showFallback, setShowFallback] = useState(src === PRODUCT_IMAGE_PLACEHOLDER_URL);
    const [loadedSuccessfully, setLoadedSuccessfully] = useState(false);

    const onLoadingComplete = (result: { naturalWidth: number; naturalHeight: number }) => {
        // Only consider it a success if width and height are non-zero
        if (result.naturalWidth > 1 && result.naturalHeight > 1) {
            setLoadedSuccessfully(true);
            setShowFallback(false);
        }
    };

    const onError = () => {
        console.warn("Image failed to load:", src);
        setLoadedSuccessfully(false);
        setShowFallback(true);
    };

    useIsomorphicLayoutEffect(() => {
        if (
            isContentfulImage(src) ||
            isAprimoImage(src) ||
            isFlashCDNImage(src) ||
            isWistiaImage(src) ||
            isStaticImage(src)
        ) {
            // Assume image will load — fallback will trigger `onError` if not
            setShowFallback(false);
        }
    }, [src]);

    return { showFallback: !loadedSuccessfully && showFallback, onLoadingComplete, onError };
};

export const generateSizesString = (sizesArray?: string[]): string | undefined => {
    if (!sizesArray || sizesArray.length === 0) {
        return "100vw";
    }

    // Create responsive sizes format based on device breakpoints
    const formattedSizes = sizesArray.map((size, index) => {
        // Skip media query for the last item (default size)
        if (index === sizesArray.length - 1) {
            return size;
        }

        // Map to appropriate device size breakpoint
        const breakpoint = deviceSizes[Math.min(index, deviceSizes.length - 1)];
        return `(max-width: ${breakpoint}px) ${size}`;
    });

    return formattedSizes.join(", ");
};

export const contentfulLoaderDefaultProps: ContentfulImageParams = {
    fm: "avif",
    q: 80,
};

const unoptimizedImageLoader = ({ src }: ImageLoaderProps) => src;

export const NextImage = forwardRef<NextImageProps, typeof Box>((props, ref) => {
    const {
        alt = "",
        src,
        width,
        height,
        loader,
        onError,
        quality,
        priority,
        loading,
        lazyRoot,
        sizes,
        style,
        lazyBoundary,
        placeholder,
        blurDataURL,
        unoptimized,
        onLoadingComplete,
        // These are the props for the fastly image optimizer
        fastlyImageParams,
        fill,
        objectFit = "contain",
        // The rest are set on <Box/>
        ...rest
    } = props;

    const parsedSrc = protocolRelativeToAbsoluteUrl(src as string);
    const isUnoptimizedImage = unoptimized || isWistiaImage(parsedSrc);

    const {
        showFallback,
        onLoadingComplete: onFallbackLoadingComplete,
        onError: onFallbackError,
    } = useImageFallback(parsedSrc);

    // This is the default image size to use if no width or height is provided (smallest mobile first approach)
    const defaultImageSize = deviceSizes[0];

    const sizesAttribute = generateSizesString(sizes);

    const imgProps = {
        alt,
        src: parsedSrc,
        width: !fill ? (width ?? defaultImageSize) : undefined,
        height: !fill ? (height ?? defaultImageSize) : undefined,
        sizes: sizesAttribute,
        style,
        loader,
        loading,
        onError,
        quality,
        priority,
        lazyRoot,
        blurDataURL,
        placeholder,
        unoptimized: isUnoptimizedImage,
        lazyBoundary,
        onLoadingComplete,
    };

    const defaultLoader = (props: ImageLoaderProps) =>
        isAprimoImage(parsedSrc) || isFlashCDNImage(parsedSrc)
            ? aprimoLoader(
                  {
                      src: parsedSrc,
                      width: props.width, // Use props.width for srcset entries
                      quality: props.quality,
                  },
                  fastlyImageParams
              )
            : contentfulLoader(
                  {
                      src: parsedSrc,
                      width: props.width, // Use props.width for srcset entries
                  },
                  {
                      fm: contentfulLoaderDefaultProps.fm,
                      q: props.quality ?? contentfulLoaderDefaultProps.q,
                  }
              );

    const handleImageLoad = (event: SyntheticEvent<HTMLImageElement, Event>) => {
        const target = event.target as HTMLImageElement;
        const result = {
            naturalWidth: target.naturalWidth,
            naturalHeight: target.naturalHeight,
        };

        if (onLoadingComplete) {
            onLoadingComplete(result);
        } else if (onFallbackLoadingComplete) {
            onFallbackLoadingComplete(result);
        }
    };

    return (
        <>
            {showFallback || isPlaceholderImage(parsedSrc) ? (
                <FallbackImage
                    aria-label={imgProps.alt}
                    width={width}
                    maxWidth="100%"
                    aspectRatio={["4/5"]}
                    backgroundColor={props.backgroundColor}
                />
            ) : (
                <Box
                    position="relative"
                    overflow="hidden"
                    height="100%"
                    width="100%"
                    ref={ref}
                    {...rest}
                >
                    {parsedSrc && (
                        <_NextImage
                            {...imgProps}
                            fill={!width && !height && fill}
                            style={{ objectFit, bottom: 0 }}
                            onLoad={handleImageLoad}
                            onError={onError || onFallbackError}
                            loader={
                                isUnoptimizedImage
                                    ? unoptimizedImageLoader
                                    : loader || defaultLoader
                            }
                        />
                    )}
                </Box>
            )}
        </>
    );
});
